import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /**
   * Cancellation scope for the single in-flight #playWhenReady attempt, so a pause (or a
   * newer play attempt) can disarm a pending canplay retry.
   * @type {AbortController | null}
   */
  #pendingPlay = null;

  connectedCallback() {
    super.connectedCallback();

    // A class field is only initialized once, but disconnectedCallback aborts this controller —
    // so a node that gets moved rather than rebuilt (the slideshow relocates slides when the
    // carousel wraps) came back with an already-aborted signal and silently lost every listener
    // below, playback tracking included. Start a fresh scope on each connect.
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    this.#trackPlaybackState();
    this.#restartAutoplay();
  }

  /**
   * Kicks off autoplay for a video that entered the DOM outside of regular
   * page parsing (e.g. the media gallery being swapped on a variant change).
   * Adopted nodes don't retain the parser's muted/autoplay handling in
   * Safari, so re-assert muted and play explicitly. On initial page load the
   * video children may not be parsed yet when this runs — that's fine, the
   * parser-built video autoplays natively.
   */
  #restartAutoplay() {
    if (!this.hasAttribute('autoplay')) return;

    // querySelector does not reach into <template> content, so this only
    // matches a video that is actually rendered.
    const video = this.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.play().catch(() => {});
    this.isPlaying = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#pendingPlay?.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // The muted content attribute only initializes defaultMuted; on nodes cloned out of a
    // <template>, Safari leaves the muted property false. Safari evaluates its autoplay /
    // inline policy the moment the element is inserted, so this has to happen BEFORE the
    // appendChild below — asserting it afterwards is too late and playback stays blocked.
    if (content instanceof HTMLVideoElement && content.hasAttribute('muted')) {
      content.muted = true;
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      // Playback tracking is already armed on the host from connectedCallback, so the clone we
      // just appended is covered without re-registering anything here.

      // `autoplay` is a boolean attribute, so getAttribute() returns '' (falsy) when it's
      // present — hasAttribute() is what actually detects it.
      if (content.hasAttribute('autoplay')) {
        this.#playWhenReady(content);
      }
    }
  }

  /**
   * Mirrors the element's real playback state onto `isPlaying` and the play/pause hint, so the
   * UI can't claim to be playing while the video sits stopped.
   *
   * `play`/`pause`/`ended` don't bubble, so listen during the capture phase — and listen on the
   * host rather than on a single video node, so whichever element actually plays is covered: the
   * copy rendered inline for autoplay, or the clone appended out of the `<template>`.
   */
  #trackPlaybackState() {
    const { signal } = this.#abortController;

    this.addEventListener('play', () => this.#setPlaybackState(true), { signal, capture: true });
    this.addEventListener('pause', () => this.#setPlaybackState(false), { signal, capture: true });

    // A video that runs to its end is stopped, not playing, so the control has to go back to
    // Play. `pause` normally fires just before `ended` and would cover it, but Safari has been
    // reported to leave `paused` false at the end of playback — so treat `ended` as a stop in
    // its own right instead of relying on a `pause` that may never arrive. Without this the
    // control kept showing Pause over a video that had already finished.
    this.addEventListener('ended', () => this.#setPlaybackState(false), { signal, capture: true });
  }

  /**
   * Records real playback state and reflects it in the play/pause hint.
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  #setPlaybackState(isPlaying) {
    this.isPlaying = isPlaying;

    // Only once the deferred content has been revealed, matching pauseMedia. updatePlayPauseHint
    // un-hides the toggle button, and on surfaces that autoplay an inline video without ever
    // revealing that button (video cards) it is a full-bleed overlay that would start swallowing
    // clicks on the card.
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(isPlaying);
    }
  }

  /**
   * Plays a video that may not have loaded yet. A `play()` issued in the same tick as the
   * insertion hits an element that is still NETWORK_EMPTY; Safari drops that request when
   * resource selection starts (Chrome keeps it queued), so retry once there's data.
   * @param {HTMLVideoElement} video
   */
  #playWhenReady(video) {
    // Only one retry may ever be armed: a newer play attempt supersedes it, and pauseMedia
    // disarms it. Without this, a pause issued while the video was still buffering would be
    // overridden when canplay finally fired and the queued play() resurrected the video.
    this.#pendingPlay?.abort();
    const pending = new AbortController();
    this.#pendingPlay = pending;

    const giveUp = () => {
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
    };

    video.play().catch(() => {
      if (pending.signal.aborted) return;

      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        // Data was there, so this wasn't a readiness problem — nothing to retry.
        giveUp();
        return;
      }

      video.addEventListener('canplay', () => video.play().catch(giveUp), {
        once: true,
        signal: pending.signal,
      });
    });
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) this.#playWhenReady(video);
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    // A pause always wins over a pending deferred play retry.
    this.#pendingPlay?.abort();

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
